import {useFormik} from 'formik';
import * as Yup from 'yup';
import {AxiosError} from 'axios';
import {CategoryGroupModel, EditCategoryGroupDTO, useCategoryGroups} from '../../../entities';
import {useSchemes} from '../../../shared';

interface UseEditCategoryGroupFormProps {
  setOpen: (open: boolean) => void;
  categoryGroup: CategoryGroupModel | null;
}

interface EditFormValues extends EditCategoryGroupDTO {}

export const useEditCategoryGroupForm = ({setOpen, categoryGroup}: UseEditCategoryGroupFormProps) => {
  const {updateCategoryGroupMutation} = useCategoryGroups();
  const {nameSchema, orderSchema} = useSchemes();

  const onSubmit = async (values: EditFormValues) => {
    if (!categoryGroup) return;

    const hasNewUpload = Boolean(values.imageUploadId);
    try {
      await updateCategoryGroupMutation.mutateAsync({
        categoryGroupId: categoryGroup.id,
        name: values.name,
        order: values.order,
        imageFileName: hasNewUpload ? null : values.imageFileName || null,
        imageUploadId: hasNewUpload ? values.imageUploadId : null,
      });
      setOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik<EditFormValues>({
    initialValues: {
      name: categoryGroup?.name || '',
      order: categoryGroup?.order ?? 0,
      imageFileName: categoryGroup?.imageFileName ?? null,
      imageUploadId: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: nameSchema,
      order: orderSchema,
      imageFileName: Yup.string().nullable(),
      imageUploadId: Yup.string().nullable(),
    }),
    onSubmit,
  });

  return {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    setFieldValue: formik.setFieldValue,
    setFieldTouched: formik.setFieldTouched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
    resetForm: formik.resetForm,
    submitForm: formik.submitForm,
    isLoading: updateCategoryGroupMutation.isPending,
    submitError: updateCategoryGroupMutation.error
      ? (updateCategoryGroupMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
  };
};
